import * as Yup from 'yup';
import i18n from 'i18next';
import STRING_KEYS from '../../../language/keys';
import { Account } from '../../../@types/Account';
import { AccountRecruitingUsageType } from '../../../consts/enums';

export const createAccountSchema = ():Yup.AnySchema => {
  return Yup.object().shape({
    subDomainName: Yup.string().matches(
      // ^(\w|\.|-)+$
      /^[0-9a-zA-Z]+(?:-[0-9a-zA-Z]+)*$/,
    ).required(i18n.t(STRING_KEYS.ACCOUNT_SUBDOMAIN_INVALID)),
    displayName: Yup.string().required(i18n.t(STRING_KEYS.ACCOUNT_REQUIRED)),
    allowedEmailDomain:
      Yup.string()
        .matches(
          /^[0-9a-zA-Z]+(?:-[0-9a-zA-Z]+)*\.[0-9a-zA-Z]+(?:-[0-9a-zA-Z]+)*(?:\.[0-9a-zA-Z]+(?:-[0-9a-zA-Z]+)*)*$/,  // x.x(.x....)
          `${i18n.t(STRING_KEYS.ALLOWED_WRONG_EMAIL_DOMAIN)}`,
        )
        .required(i18n.t(STRING_KEYS.ALLOWED_WRONG_EMAIL_DOMAIN)),
    logo: Yup.string(),
    maximumOpenPositions: Yup.number(),
    companyInfo: Yup.object().shape({
      city: Yup.string(),
      country: Yup.string(),
      state: Yup.string(),
      name: Yup.string().required(i18n.t(STRING_KEYS.ACCOUNT_COMPANY_NAME_REQUIRED)),
      size: Yup.string().required(i18n.t(STRING_KEYS.ACCOUNT_COMPANY_SIZE_REQUIRED)),
    }),
  });
};
export const getUpdateAccountSchema = (account:Account): Yup.AnySchema => Yup.object().shape({
  subDomainName: Yup.string().matches(
    // ^(\w|\.|-)+$
    /^[0-9a-zA-Z]+(?:-[0-9a-zA-Z]+)*$/,
  ).required(i18n.t(STRING_KEYS.ACCOUNT_SUBDOMAIN_INVALID)),
  displayName: Yup.string().required(i18n.t(STRING_KEYS.ACCOUNT_REQUIRED)),
  logo: Yup.string(),
  recruitingUsageType: Yup.string().oneOf(Object.values(AccountRecruitingUsageType), 'Invalid recruiting type'),
  companyInfo: Yup.object().shape({
    city: Yup.string(),
    country: Yup.string(),
    state: Yup.string(),
    name: Yup.string().required(i18n.t(STRING_KEYS.ACCOUNT_COMPANY_NAME_REQUIRED)),
    size: Yup.string().required(i18n.t(STRING_KEYS.ACCOUNT_COMPANY_SIZE_REQUIRED)),
  }),
  maximumOpenPositions: Yup.number(),
  maxUserSeats: Yup.number().when([], {
    is: () => account?.plan?.planDefinition?.userSeatsPlanLimit != null,
    then: (schema) => schema.max(
      account.plan.planDefinition.userSeatsPlanLimit,
      `Cannot be higher than the plan limit (${account.plan.planDefinition.userSeatsPlanLimit})`,
    ),
    otherwise: (schema) => schema,
  }),
  addCredits: Yup.number().min(0),
  startDatePaidPlan: Yup.date()
    .nullable()
    // Empty string from formik would fail Yup.date() type-coercion before
    // the `when` clause runs, so the conditional `.notRequired()` below
    // never gets a chance to allow it. Normalize '' → null first.
    .transform((curr, orig) => (orig === '' ? null : curr))
    .when('pricingPlan', {
      is: (val: string) => val !== 'LITE' && val !== 'PAY_PER_INTERVIEW',
      then: (schema) => schema.required('Start date is required'),
      otherwise: (schema) => schema.notRequired(),
    }),
  endDatePaidPlan: Yup.date()
    .nullable()
    .transform((curr, orig) => (orig === '' ? null : curr))
    .when('pricingPlan', {
      is: (val: string) => val !== 'LITE' && val !== 'PAY_PER_INTERVIEW',
      then: (schema) => schema.required('End date is required'),
      otherwise: (schema) => schema.notRequired(),
    }),
});

export const changeCompanySchema = ():Yup.AnySchema => {
  return Yup.object().shape({
    logo: Yup.string(),
    companyInfo: Yup.object().shape({
      city: Yup.string(),
      country: Yup.string(),
      state: Yup.string(),
      name: Yup.string().required(i18n.t(STRING_KEYS.ACCOUNT_COMPANY_NAME_REQUIRED)),
      size: Yup.string().required(i18n.t(STRING_KEYS.ACCOUNT_COMPANY_SIZE_REQUIRED)),
    }),
  });
};
